import { defaultGroupedFilter } from 'module/types/filter-field/grouped-filter/filter';
import { FilterGroupType } from 'module/types/filter-field/grouped-filter/group';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import {
  ExpandableSearchInput,
  ExpandableSearchInputRef,
} from 'module/ui/components/atoms/input/search-input';
import { CenterAlignedBox } from 'module/ui/components/atoms/layout/layout';
import { PageLoading } from 'module/ui/components/atoms/loading';
import { MoreActionsDropdown } from 'module/ui/components/atoms/more-actions';
import { GroupedFilterPopover } from 'module/ui/components/molecules/field-filter/popover/grouped-filter-popover/popover';
import { faDownload } from '@fortawesome/pro-regular-svg-icons';
import { useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { AccountPeopleList } from 'components/organisms/account-people/list';
import { PeopleListGroupBy } from 'components/organisms/account-people/list/group-by';
import { useAccountPeopleFieldsForFilter } from 'components/organisms/account-people/list/use-filter-fields';
import { usePeoplePageGroupByFields } from 'components/organisms/account-people/list/use-group-by-fields';
import { useAccountPeoplePageParams } from 'components/organisms/account-people/utils/use-page-params';
import { AccountPeopleViewSwitcher } from 'components/organisms/account-people/view-switcher';
import { AccountLayout } from '../common/layout';
import { AccountsTabs, AccountsTabsType } from '../common/tab';
import { useCompanyFromUrl } from '../use-current-company';

const ActionBarWrapper = styled(CenterAlignedBox)``;

export const AccountPeopleListPage = () => {
  const { data: company, isLoading: isCompanyLoading } = useCompanyFromUrl();
  const { fields, isLoading: isFieldsLoading } =
    useAccountPeopleFieldsForFilter();
  const { params, onParamsChange } = useAccountPeoplePageParams();
  const { fields: groupByFields, loading: isGroupByFieldsLoading } =
    usePeoplePageGroupByFields();
  const searchInputRef = useRef<ExpandableSearchInputRef>(null);

  return isCompanyLoading || company == null ? (
    <PageLoading />
  ) : (
    <AccountLayout
      company={company}
      tabs={<AccountsTabs activeKey={AccountsTabsType.People} />}
      content={
        <AccountPeopleList
          pageParams={params}
          company={company}
          clearSearch={() => {
            onParamsChange({ searchQuery: '' });
            searchInputRef.current?.closeSearchInput();
          }}
        />
      }
      actions={{
        primary: (
          <ActionBarWrapper>
            <AccountPeopleViewSwitcher />
            <GroupedFilterPopover
              fields={fields}
              filter={params.filter}
              onChange={(next) => onParamsChange({ filter: next })}
              loading={isFieldsLoading}
              reset={() => {
                onParamsChange({
                  filter: defaultGroupedFilter(FilterGroupType.User),
                });
                searchInputRef.current?.closeSearchInput();
              }}
              filtersChanged={false}
              showSaveView={false}
            />
            <PeopleListGroupBy
              fields={groupByFields}
              value={params.groupBy}
              onChange={(next) => onParamsChange({ groupBy: next })}
              loading={isGroupByFieldsLoading}
            />
          </ActionBarWrapper>
        ),
        secondary: (
          <ActionBarWrapper>
            <ExpandableSearchInput
              ref={searchInputRef}
              direction="right"
              value={params.searchQuery}
              onChange={(event) =>
                onParamsChange({ searchQuery: event.target.value })
              }
              searchButtonProps={{
                type: 'text',
              }}
              minWidth="360px"
            />
            <MoreActionsDropdown
              menu={{
                items: [
                  {
                    type: 'group',
                    label: <FormattedMessage id="more_actions" />,
                    children: [
                      {
                        key: 'export',
                        label: <FormattedMessage id="export" />,
                        icon: <FontAwesomeIcon icon={faDownload} size={12} />,
                      },
                    ],
                  },
                ],
              }}
            />
          </ActionBarWrapper>
        ),
      }}
    />
  );
};
